/**
 * choose-size.js — opens a size dropdown on the product card and adds the
 * chosen variant to the cart without a full page reload.
 *
 * Mirrors the old theme's inline-quickshop ajax-dropdown / ajax-submit flow
 * with Horizon-vanilla code (no jQuery, no Tailwind). Listens once at document
 * level so the bindings survive cart-items-component / morphSection re-renders.
 *
 * Dispatches a `cart:update` CustomEvent in the same shape Horizon's
 * cart-items-component already consumes, so the cart drawer/cart will refresh
 * after each add.
 */

import { ThemeEvents, CartAddEvent } from '@theme/events';
import { getRememberedSize, rememberSize } from '@theme/size-memory';

const TRIGGER_SELECTOR = '[data-choose-size-trigger]';
const PANEL_SELECTOR = '[data-choose-size-panel]';
const OPTION_SELECTOR = '.choose-size__option';
const SINGLE_ID_SELECTOR = '[data-choose-size-single-id]';
const COMPONENT_SELECTOR = 'choose-size-component';
const ERROR_SELECTOR = '[data-choose-size-error]';

function setPanelOpen(component, isOpen) {
  const size = getRememberedSize(component.dataset.sizeGroup);
  if (size) {
    component.querySelectorAll(OPTION_SELECTOR).forEach((option) => {
      const selected = option.textContent.trim().toUpperCase() === size;
      option.classList.toggle('is-selected', selected);
      option.setAttribute('aria-pressed', String(selected));
    });
  }
  const panel = component.querySelector(PANEL_SELECTOR);
  const trigger = component.querySelector(TRIGGER_SELECTOR);
  if (panel) panel.hidden = !isOpen;
  if (trigger) trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

  component.classList.toggle('is-open', isOpen);
  component.closest('product-card')?.classList.toggle('choose-size-card-open', isOpen);
  component.closest('.product-grid__item')?.classList.toggle('choose-size-card-open', isOpen);
}

function getCartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    const id = el instanceof HTMLElement ? el.dataset.sectionId : null;
    if (id) ids.add(id);
  });
  return Array.from(ids).join(',');
}

function getCartAddUrl() {
  return (
    (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js'
  );
}

function closeAllPanels(except) {
  document.querySelectorAll(`${COMPONENT_SELECTOR}`).forEach((component) => {
    if (component === except) return;
    const panel = component.querySelector(PANEL_SELECTOR);
    if (panel && !panel.hidden) setPanelOpen(component, false);
  });
}

function showError(component, message) {
  const errorEl = component.querySelector(ERROR_SELECTOR);
  if (!errorEl) return;
  errorEl.textContent = message;
  errorEl.hidden = false;
  setTimeout(() => {
    errorEl.hidden = true;
    errorEl.textContent = '';
  }, 5000);
}

function setLoading(trigger, isLoading) {
  if (!(trigger instanceof HTMLElement)) return;
  if (isLoading) {
    trigger.dataset.loading = 'true';
    trigger.setAttribute('disabled', 'disabled');
    trigger.setAttribute('aria-busy', 'true');
  } else {
    delete trigger.dataset.loading;
    trigger.removeAttribute('disabled');
    trigger.removeAttribute('aria-busy');
  }
}

async function addVariantToCart({ component, variantId, trigger }) {
  const cartAddUrl = getCartAddUrl();
  const formData = new FormData();
  formData.set('id', String(variantId));
  formData.set('quantity', '1');
  const sectionIds = getCartSectionIds();
  if (sectionIds) formData.set('sections', sectionIds);

  setLoading(trigger, true);

  try {
    const response = await fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    });
    const payload = await response.json();

    if (!response.ok || payload.status) {
      const message = payload.description || payload.message || 'Could not add to cart.';
      showError(component, message);
      return;
    }

    // Fire the same event the rest of the theme listens for so cart drawer
    // and cart-items-component refresh themselves.
    const sourceId = component.id || component.dataset.productId || '';
    document.dispatchEvent(
      new CartAddEvent(payload, sourceId, {
        source: 'choose-size',
        productId: component.dataset.productId,
        variantId: String(payload.variant_id || variantId),
        itemCount: payload.items_count,
        sections: payload.sections,
      })
    );

    // Close this panel after success
    setPanelOpen(component, false);
  } catch (error) {
    console.error('[choose-size] network error', error);
    showError(component, 'Network error. Please try again.');
  } finally {
    setLoading(trigger, false);
  }
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  // Click on a size option → add that variant
  const option = target.closest(OPTION_SELECTOR);
  if (option) {
    if (option.classList.contains('is-disabled') || option.hasAttribute('aria-disabled')) return;
    const component = option.closest(COMPONENT_SELECTOR);
    if (!(component instanceof HTMLElement)) return;
    const trigger = component.querySelector(TRIGGER_SELECTOR);
    const variantId = option.getAttribute('data-variant-id');
    if (!variantId) return;
    rememberSize(component.dataset.sizeGroup, option.textContent, component);
    // A product card listens for bubbled clicks to navigate to its PDP. Options
    // are list items rather than native controls, so cancel the event before it
    // reaches that card listener; the selected variant is added via AJAX below.
    event.preventDefault();
    event.stopPropagation();
    addVariantToCart({ component, variantId, trigger });
    return;
  }

  // Click on the trigger button
  const trigger = target.closest(TRIGGER_SELECTOR);
  if (trigger) {
    if (trigger.hasAttribute('disabled')) return;
    const component = trigger.closest(COMPONENT_SELECTOR);
    if (!(component instanceof HTMLElement)) return;
    event.preventDefault();
    event.stopPropagation();

    // Single-variant path — straight add to cart.
    const singleIdInput = component.querySelector(SINGLE_ID_SELECTOR);
    if (singleIdInput instanceof HTMLInputElement && singleIdInput.value) {
      addVariantToCart({ component, variantId: singleIdInput.value, trigger });
      return;
    }

    const panel = component.querySelector(PANEL_SELECTOR);
    if (!panel) return;
    const willOpen = panel.hidden;
    closeAllPanels(component);
    setPanelOpen(component, willOpen);
    return;
  }

  // Click anywhere else closes any open panel.
  closeAllPanels(null);
}, true);

// ESC closes any open panel.
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  closeAllPanels(null);
});

// Lightweight custom element — registering it lets us style/scope, but the
// actual logic lives in the document-level listeners above so it survives
// section re-renders.
if (!customElements.get('choose-size-component')) {
  customElements.define(
    'choose-size-component',
    class extends HTMLElement {}
  );
}

// Reference exported symbol so module bundlers keep the import.
export { ThemeEvents };
